// apps/client/src/components/layout/UnifiedTopBar.tsx
// Single-row top bar with segmented control (for Reports) and page tabs

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, User, Lock, Building2, FileText, Settings, Target } from 'lucide-react';
import { useCommandPalette } from '../command-palette';
import {
  SubscriptionTier,
  tierHasFeature,
  FEATURE_REGISTRY,
  Feature,
  isFeatureEnabled,
} from '../../config/subscriptionTiers';
import {
  ADMIN_REPORT_ROUTE_ROLES,
  REPORT_REGISTRY,
  isReportSuppressedForTenant,
  type TenantSuppressibleReportFlag,
} from '../../config/reportRegistry';
import {
  officeAdminReportGrantsApply,
  topBarPortfolioItems,
  type ReportAccessContext,
} from '../../hooks/reports/resolveReportAccess';
import { adminPathDenied } from '../../config/adminAccess';
import { canUseLinkedDeals } from '../../hooks/reports/useLinkedDeals';

interface UnifiedTopBarProps {
  companyTier?: SubscriptionTier | null;
  userRole?: string;
  hasAcquiredBooks?: boolean;
  offices?: any[];
  userData?: any;
  isDemo?: boolean;
  betaFeatures?: string[];
  /** Per-company feature grants (additive override, independent of tier). Mirrors `companyData.featureFlags`. */
  grantedFeatures?: Record<string, boolean>;
  /** companies/{id}.aggregator: decides aggregator-specific tabs (aggregatorProfiles.ts). */
  aggregator?: unknown;
}

interface NavItem {
  label: string;
  to: string;
  feature?: Feature;
}

interface SubSection {
  id: string;
  label: string;
  items: NavItem[];
}

type SectionId = 'reports' | 'offices' | 'settings' | 'broker-activity';

interface SectionConfig {
  id: SectionId;
  label: string;
  icon: React.ReactNode;
  subSections?: SubSection[];
  items?: NavItem[];
}

export const UnifiedTopBar: React.FC<UnifiedTopBarProps> = ({
  companyTier,
  userRole,
  hasAcquiredBooks = false,
  offices = [],
  userData,
  isDemo = false,
  betaFeatures = [],
  grantedFeatures = {},
  aggregator,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { open: openCommandPalette } = useCommandPalette();

  const hasFeature = (feature: Feature): boolean => {
    // Per-company override (additive grant): mirrors backend `featureGate.ts`.
    if (grantedFeatures[feature] === true) return true;
    if (!companyTier) return false;
    return tierHasFeature(companyTier, feature);
  };

  const getRequiredTier = (feature: Feature): SubscriptionTier | undefined => {
    return FEATURE_REGISTRY[feature]?.requiredTier;
  };

  // Per-tenant report suppression (fail-open): a report drops out of the nav ONLY
  // when the tenant explicitly sets `featureFlags.<flag> = false` (an aggregator
  // that can never populate it). Absent flag = visible, exactly as today.
  const isReportSuppressed = (flag: TenantSuppressibleReportFlag): boolean =>
    isReportSuppressedForTenant(grantedFeatures, flag);

  const tierBadgeColors: Record<SubscriptionTier, string> = {
    plus: 'bg-cyan-500/20 text-cyan-400',
    pro: 'bg-purple-500/20 text-purple-400',
    enterprise: 'bg-amber-500/20 text-amber-400',
    network: 'bg-slate-500/20 text-slate-300',
  };

  // Section-specific accent colors for active tabs
  const sectionColors: Record<SectionId, {
    activeBg: string;
    activeBorder: string;
    activeText: string;
    activeShadow: string;
  }> = {
    reports: {
      activeBg: 'bg-purple-500/15',
      activeBorder: 'border border-purple-500/30',
      activeText: 'text-white',
      activeShadow: 'shadow-[0_0_10px_rgba(139,92,246,0.2)]',
    },
    offices: {
      activeBg: 'bg-blue-500/15',
      activeBorder: 'border border-blue-500/30',
      activeText: 'text-white',
      activeShadow: 'shadow-[0_0_10px_rgba(59,130,246,0.2)]',
    },
    settings: {
      activeBg: 'bg-gray-500/15',
      activeBorder: 'border border-gray-500/30',
      activeText: 'text-white',
      activeShadow: 'shadow-[0_0_10px_rgba(107,114,128,0.2)]',
    },
    'broker-activity': {
      activeBg: 'bg-blue-500/15',
      activeBorder: 'border border-blue-500/30',
      activeText: 'text-white',
      activeShadow: 'shadow-[0_0_10px_rgba(59,130,246,0.2)]',
    },
  };

  // Granular report access for restricted brokers. The registry-backed reports
  // (`inTopBarGating`) are sourced from the shared report registry (single source
  // of truth); `approved-vs-settled` is a top-bar-only gate that is intentionally
  // NOT a registry report (this is the documented per-site divergence: it stays
  // gated so restricted brokers never see it), so it is added explicitly here.
  const pathToReportSlug: Record<string, string> = {
    ...Object.fromEntries(
      REPORT_REGISTRY.filter((def) => def.inTopBarGating).map((def) => [def.path, def.slug]),
    ),
    '/reports/approved-vs-settled': 'approved-vs-settled',
  };

  const isBrokerFamilyRole = userRole === 'broker' || userRole === 'broker_assistant';
  const isAdminReportRole = ADMIN_REPORT_ROUTE_ROLES.includes(userRole || '');
  // Marketing is denied Reports, Offices and Settings (incl. /help), so those
  // mega-menu sections are not built for them. They keep Lead Capture and
  // Broker Activity (marketing RBAC §6 nav lockdown).
  const isMarketing = userRole === 'marketing';

  const hasReportAccess = (path: string): boolean => {
    // An administrator's report deny-list (config/adminAccess.ts). Inert for
    // every other role and for an unrestricted administrator.
    if (adminPathDenied(userData, path)) return false;
    // An office_admin carrying a non-empty allowedReports list is filtered by
    // the same granular map. `isBrokerFamilyRole` is deliberately NOT widened:
    // it also strips Trail Run-Off, Trail Book Valuation, Trail Forecast, RCTI
    // Cashflow and the whole Offices section, which office_admin keeps.
    if (!isBrokerFamilyRole && !officeAdminReportGrantsApply(userRole, userData?.allowedReports)) {
      return true;
    }
    const allowed = userData?.allowedReports;
    if (!Array.isArray(allowed) || allowed.length === 0) return true;
    const slug = pathToReportSlug[path];
    if (!slug) return true; // Non-gated reports (trail-run-off, etc.) — always visible for non-restricted
    return allowed.includes(slug);
  };

  const filterItems = <T extends { to: string }>(items: T[]): T[] =>
    items.filter(item => hasReportAccess(item.to));

  // Access context for the shared report resolver. The Portfolio sub-section is
  // built entirely by resolveReportAccess.topBarPortfolioItems (the golden-verified
  // selector), which reproduces the former inline Portfolio construction (incl.
  // the `book_value_report` / `broker_book_trend_report === 'enabled'` tri-state
  // gates and the granular broker filter) byte-for-byte.
  const reportContext: ReportAccessContext = {
    role: userRole,
    tier: companyTier ?? null,
    featureFlags: grantedFeatures,
    betaFeatures,
    allowedReports: userData?.allowedReports,
    hideCommissions: false,
    hasAcquiredBooks,
  };

  // Define section configurations
  const sections: Partial<Record<SectionId, SectionConfig>> = useMemo(() => ({
    // Reports: denied to marketing (commission/loan/customer data).
    ...(!isMarketing ? {
    reports: {
      id: 'reports' as SectionId,
      label: 'Reports',
      icon: <FileText className="w-5 h-5" />,
      subSections: [
        {
          id: 'trail-book',
          label: 'Trail Book',
          items: [
            // Trail Run-Off, Trail Book Valuation, Trail Forecast: admin report routes only
            // (credit_analyst included in the refusal, not just the broker family)
            ...(isAdminReportRole
              ? [
                  { label: 'Broker Trail Run-Off', to: '/reports/trail-run-off' },
                ]
              : []),
            { label: 'Loan Trail Drop-Off', to: '/reports/trail-drop-off', feature: 'trail_drop_off' },
            ...(isAdminReportRole
              ? [
                  { label: 'Trail Book Valuation', to: '/reports/trail-book-valuation', feature: 'trail_book_valuation' as Feature },
                  { label: 'Trail Forecast', to: '/reports/trail-forecast', feature: 'trail_forecast' as Feature },
                ]
              : []),
            { label: 'Book Health', to: '/reports/book-health', feature: 'book_health' },
            // Succession Readiness - beta flag AND an admin report role. Without the
            // role check a broker whose grants hide Trail Drop-Off and Book Health got
            // it as the first Trail Book item, so the tab click bounced them to "/".
            ...(betaFeatures.includes('succession_readiness') && isAdminReportRole
              ? [{ label: 'Succession Readiness', to: '/reports/succession-readiness', feature: 'succession_readiness' as Feature }]
              : []),
            { label: 'Discharged Loans', to: '/reports/discharged-loans' },
            // Trail Leakage - super_admin + administrator only. Entitlement is a
            // UNION of trail_leakage (pro+ by tier) OR broker_activity (decoupled 2026-07-20).
            // Entitlement enforcement happens server-side + in FeatureGate on the page;
            // nav shows for those roles so they can discover the feature, UNLESS the
            // tenant explicitly suppressed it (fail-open, see reportRegistry) - a tenant
            // holding neither side of the union only ever reaches the upgrade prompt.
            ...(['super_admin', 'administrator'].includes(userRole || '') &&
            !isReportSuppressed('trail_leakage')
              ? [{ label: 'Trail Leakage', to: '/reports/trail-leakage' }]
              : []),
          ],
        },
        {
          id: 'income-payments',
          label: 'Income & Payments',
          items: [
            { label: 'Settlement Age', to: '/reports/settlement-age' },
            { label: 'Clawbacks', to: '/reports/clawbacks' },
            // Arrears + Approved vs Settled: hidden only for tenants that
            // explicitly suppress them (fail-open, see reportRegistry).
            ...(!isReportSuppressed('arrears')
              ? [{ label: 'Arrears', to: '/reports/arrears' }]
              : []),
            ...(!isReportSuppressed('approved_vs_settled')
              ? [{ label: 'Approved vs Settled', to: '/reports/approved-vs-settled' }]
              : []),
            // Linked deals: only for tenants whose aggregator offers it AND whose
            // featureFlags.linked_deals is 'enabled', and only for company-wide
            // confirmers (canUseLinkedDeals). Server enforces again.
            ...(canUseLinkedDeals(userData, { aggregator, featureFlags: grantedFeatures })
              ? [{ label: 'Linked deals', to: '/reports/linked-deals' }]
              : []),
            // RCTI Cashflow: admin report routes only
            ...(isAdminReportRole
              ? [{ label: 'RCTI Cashflow', to: '/reports/rcti-cashflow', feature: 'rcti_cashflow' as Feature }]
              : []),
            // Group Snapshot - super_admin ONLY, and only for tenants explicitly
            // enabled via `featureFlags.company_overview_share` (default OFF, so
            // this is dark for every other tenant). Deliberately gated on the flag
            // rather than a company id: no hardcoded tenant allow-lists.
            // It mints a PUBLIC, unauthenticated share link over group-wide
            // commission figures, which is why administrator/office_admin do not
            // see it even though they can see most of this section. Enforced again
            // server-side on both companyShare callables - the nav gate alone is
            // never the control.
            ...(userRole === 'super_admin' && grantedFeatures.company_overview_share === true
              ? [{ label: 'Group Snapshot', to: '/reports/group-snapshot' }]
              : []),
          ],
        },
        {
          id: 'portfolio',
          label: 'Portfolio',
          // Built by the shared resolver: Loan/Customer Portfolio, Referral
          // Partners (beta), Client Retention, Acquired Books (acquired-books or
          // refinance_intelligence grant), Broker Loan Books (admin), Book Value
          // Report (admin + `book_value_report === 'enabled'`) and Book Trend
          // Report (tri-state flag + role + broker-family allowedReports gate),
          // then the granular broker report filter: verbatim, golden-verified.
          items: topBarPortfolioItems(reportContext),
        },
      // Apply granular report access filtering and remove empty sub-sections
      ].map(sub => ({ ...sub, items: filterItems(sub.items as NavItem[]) }))
       .filter(sub => sub.items.length > 0),
    },
    } : {}),
    // Offices section — hidden for broker family roles (use main dashboard only)
    // and for marketing (no office-level commission/loan data access).
    ...(!isBrokerFamilyRole && !isMarketing ? {
      offices: {
        id: 'offices' as SectionId,
        label: 'Offices',
        icon: <Building2 className="w-5 h-5" />,
        items: [
          ...offices.map((office: any) => ({
            label: office.isHeadOffice ? `${office.name} HQ` : office.name,
            to: `/offices/${office.id}`,
            feature: 'office_dashboard' as Feature,
          })),
          { label: 'Manage Offices', to: '/offices' },
          { label: 'Manage Brokers', to: '/brokers' },
        ],
      },
    } : {}),
    // Settings (incl. /help): denied to marketing entirely.
    ...(!isMarketing ? {
    settings: {
      id: 'settings' as SectionId,
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      items: [
        { label: 'My Settings', to: '/settings' },
        ...(['office_admin', 'administrator', 'super_admin'].includes(userRole || '')
          ? [{ label: 'Audit Log', to: '/settings/audit-log' }]
          : []),
        ...(['super_admin', 'administrator'].includes(userRole || '')
          ? [{ label: 'Loan Books', to: '/loan-books' }]
          : []),
        // Status and disconnect remain reachable after entitlement loss. The
        // Integrations page gates only connect, configure and test controls.
        ...(userRole === 'super_admin'
          ? [{ label: 'Integrations', to: '/settings/integrations' }]
          : []),
        ...(userRole === 'super_admin'
          ? [{ label: 'Billing', to: '/settings/billing' }]
          : []),
        { label: 'Help & Support', to: '/help' },
      ],
    },
    } : {}),
    // Broker Activity — Pipeline / Scorecard / Weekly Funnel render under the
    // main app shell as a section like Reports / Offices / Settings. Section
    // surfaces only when the tenant is entitled to broker_activity (the same
    // gate the sidebar uses); each page additionally enforces the FeatureGate
    // paywall, so an un-entitled deep link still shows the upgrade card with
    // no tabs rather than a broken nav. Trail Leakage is NOT here — it lives
    // under Reports. (RequireRole on the routes enforces role access.)
    ...(hasFeature('broker_activity') ? {
      'broker-activity': {
        id: 'broker-activity' as SectionId,
        label: 'Team Activity',
        icon: <Target className="w-5 h-5" />,
        items: [
          { label: 'Pipeline', to: '/broker-activity/pipeline' },
          { label: 'Scorecard', to: '/broker-activity/scorecard' },
          { label: 'Weekly Funnel', to: '/broker-activity/weekly-funnel' },
          { label: 'Lodgements', to: '/broker-activity/lodgements' },
        ],
      },
    } : {}),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [userRole, hasAcquiredBooks, offices, userData, grantedFeatures, aggregator]);

  // Determine current section and sub-section based on pathname
  const getCurrentContext = () => {
    // Reports section detection
    const trailBookPaths = ['/reports/trail-run-off', '/reports/trail-drop-off', '/reports/trail-book-valuation', '/reports/trail-forecast', '/reports/book-health', '/reports/succession-readiness', '/reports/discharged-loans', '/reports/trail-leakage'];
    const incomePaths = ['/reports/settlement-age', '/reports/clawbacks', '/reports/arrears', '/reports/rcti-cashflow', '/reports/approved-vs-settled', '/reports/group-snapshot'];
    const portfolioPaths = ['/reports/loans', '/reports/customer-portfolio', '/reports/referral-partners', '/reports/refinance-intelligence', '/reports/acquired-books', '/reports/broker-loan-books', '/reports/book-value', '/reports/book-trend'];

    if (trailBookPaths.some(p => pathname.startsWith(p))) {
      return { section: 'reports' as SectionId, subSection: 'trail-book' };
    }
    if (incomePaths.some(p => pathname.startsWith(p))) {
      return { section: 'reports' as SectionId, subSection: 'income-payments' };
    }
    if (portfolioPaths.some(p => pathname.startsWith(p))) {
      return { section: 'reports' as SectionId, subSection: 'portfolio' };
    }

    // Offices section
    if (pathname.startsWith('/offices') || pathname.startsWith('/brokers')) {
      return { section: 'offices' as SectionId, subSection: null };
    }

    // Settings section
    if (pathname.startsWith('/settings') || pathname.startsWith('/loan-books') || pathname.startsWith('/help')) {
      return { section: 'settings' as SectionId, subSection: null };
    }

    // Broker Activity section
    if (pathname.startsWith('/broker-activity')) {
      return { section: 'broker-activity' as SectionId, subSection: null };
    }

    return { section: null, subSection: null };
  };

  const { section: currentSection, subSection: currentSubSection } = getCurrentContext();

  // Get section config if we're in a section
  const sectionConfig = currentSection ? sections[currentSection] : null;
  const subSections = sectionConfig?.subSections;
  const currentSubSectionConfig = subSections?.find(s => s.id === currentSubSection);

  // Get items to display as tabs
  const tabItems = currentSubSectionConfig?.items || sectionConfig?.items || [];

  // Handle segmented control click - navigate to first page of that sub-section
  const handleSubSectionClick = (subSection: SubSection) => {
    navigate(subSection.items[0].to);
  };

  // Always show top bar - matches sidebar header height (3.5rem)
  return (
    <div className="bg-gray-900 border-b border-gray-800 px-4 h-[3.5rem] flex items-center">
      <div className="flex items-center justify-between w-full">
        {/* Left side: Section icon, segmented control/tabs */}
        <div className="flex items-center gap-3">
          {currentSection && sectionConfig && (
            <>
              {/* Section icon */}
              <div className="flex items-center gap-2 text-gray-400">
                {sectionConfig.icon}
              </div>

              {/* Segmented control for Reports (shows all 3 sub-sections) */}
              {subSections && subSections.length > 0 && (
                <div className="flex items-center bg-gray-800/50 rounded-lg p-0.5">
                  {subSections.map((sub) => {
                    const isActive = sub.id === currentSubSection;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => handleSubSectionClick(sub)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                          isActive
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                        }`}
                      >
                        {sub.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Divider - only if we have tabs */}
              {tabItems.length > 0 && <div className="h-5 w-px bg-gray-700" />}

              {/* Page tabs */}
              <div className="flex items-center gap-1 flex-nowrap overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {/* Find the most specific matching path (longest match wins) */}
                {(() => {
                  const matchingItem = tabItems
                    .filter(i => pathname === i.to || pathname.startsWith(i.to + '/'))
                    .reduce((best, current) =>
                      !best || current.to.length > best.to.length ? current : best
                    , null as NavItem | null);
                  const activeItemTo = matchingItem?.to;

                  return tabItems.map((item) => {
                  const isLocked = item.feature && !hasFeature(item.feature);
                  const requiredTier = item.feature ? getRequiredTier(item.feature) : undefined;
                  const isActive = item.to === activeItemTo;
                  const colors = currentSection ? sectionColors[currentSection] : sectionColors.reports;

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isLocked
                          ? 'text-gray-500 cursor-not-allowed border border-transparent'
                          : isActive
                            ? `${colors.activeBg} ${colors.activeBorder} ${colors.activeText} ${colors.activeShadow}`
                            : 'text-gray-400 hover:bg-gray-800/50 hover:text-white border border-transparent'
                      }`}
                    >
                      <span>{item.label}</span>
                      {isLocked && requiredTier && (
                        <span className={`flex items-center gap-0.5 px-1 py-0.5 text-[0.5625rem] font-medium rounded ${tierBadgeColors[requiredTier]}`}>
                          <Lock className="w-2 h-2" />
                        </span>
                      )}
                    </NavLink>
                  );
                });
                })()}
              </div>
            </>
          )}
        </div>

        {/* Right side: Search, notifications, profile - ALWAYS visible */}
        <div className="flex items-center gap-2">
          <button
            onClick={openCommandPalette}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Search (⌘K)"
          >
            <Search className="w-5 h-5" />
          </button>
          <button
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
          </button>
          <button
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title="Profile"
          >
            <User className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedTopBar;
